import { useCallback, useEffect, useRef, useState } from 'react';

interface UseAutoRefreshOptions {
  intervalSeconds: number;
  enabled?: boolean;
  onRefresh: () => Promise<void> | void;
}

export function useAutoRefresh({
  intervalSeconds,
  enabled = true,
  onRefresh,
}: UseAutoRefreshOptions) {
  const refreshRef = useRef(onRefresh);
  const runningRef = useRef(false);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshAt, setLastRefreshAt] = useState<Date | null>(null);

  useEffect(() => {
    refreshRef.current = onRefresh;
  }, [onRefresh]);

  const refreshNow = useCallback(async () => {
    if (runningRef.current) {
      return;
    }

    runningRef.current = true;
    setRefreshing(true);

    try {
      await refreshRef.current();
      setLastRefreshAt(new Date());
    } finally {
      runningRef.current = false;
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled || intervalSeconds <= 0) {
      return;
    }

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        void refreshNow();
      }
    }, intervalSeconds * 1000);

    return () => window.clearInterval(intervalId);
  }, [enabled, intervalSeconds, refreshNow]);

  return {
    refreshNow,
    refreshing,
    lastRefreshAt,
  };
}
